<template>
  <div>
    <Loader :isVisible="isLoading" />
    <div class="images-wrapper">
      <div
        v-if="assets.length === 0 && !isLoading && !displayEditableAssetsDuringProcessing"
        class="no-images"
      >
        <Loader
          :isVisible="assets.length === 0 && !isLoading"
          className="media-loader"
        />
        No images to display
      </div>
      <div
        v-if="shouldWarnImagesTab"
        class="unsaved-changes-warning"
      >
        <inline-svg
          :src="iconWarning"
          class="svg-icon"
        ></inline-svg>
        Changes won’t be saved unless you click Apply
      </div>
      <div
        v-for="imageAsset in assets"
        :key="imageAsset.id"
        :ref="`imageAssetItem_${imageAsset.id}`"
        class="image-container"
      >
        <Loader
          :isVisible="assetItemInProcessing === imageAsset.id"
          className="assets-loader"
        />
        <AssetImageUploader
          ref="assetImage"
          :disabled="!imageAsset.isAnyPoster"
          :isPosterInspector="true"
          imageType="Mezzanine Image"
          :source="imageAsset"
          :getMediaItemAssets="getMediaItemAssets"
          :addAssetItemWithChanges="addAssetItemWithChanges"
          :removeAssetItemWithChanges="removeAssetItemWithChanges"
          :setAssetIsInProcessing="setAssetIsInProcessing"
          :displayEditableAssetsDuringProcessing="displayEditableAssetsDuringProcessing"
          @updateMediaItem="$emit('updateMediaItem', $event)"
        />
        <AssetImageInspector
          ref="assetInfo"
          :source="imageAsset"
          :getMediaItemAssets="getMediaItemAssets"
          :setAssetIsInProcessing="setAssetIsInProcessing"
          :addAssetItemWithChanges="addAssetItemWithChanges"
          :removeAssetItemWithChanges="removeAssetItemWithChanges"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  GET_ASSET_LANGUAGE_REQUEST,
} from '../../../store/actions/media/mediaAsset';
import Loader from '../../common/Loader.vue';
import AssetImageUploader from '../../common/PosterImage/AssetImageUploader.vue';
import AssetImageInspector from '../inspector/AssetImageInspector.vue';
import iconWarning from '../../../assets/icons/icon-warning-localization.svg';
import { imageConstraints } from '../../../utils/validation/imagesValidation';

export default {
  name: 'AssetImagesList',
  components: {
    Loader,
    AssetImageUploader,
    AssetImageInspector,
  },
  props: {
    mediaItem: {
      type: Object,
      default: () => {},
    },
    getMediaItemAssets: {
      type: Function,
      default: () => {},
    },
    imageItemToHilight: {
      type: Object,
      default: null,
    },
    clearImageItemToHilight: {
      type: Function,
      default: () => {},
    },
    clearImagesTabWarn: {
      type: Function,
      default: () => {},
    },
    shouldWarnImagesTab: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iconWarning,
      assetItemInProcessing: null,
      itemsWithChanges: [],
    };
  },
  computed: {
    ...mapState({
      isLoading: (state) => state.mediaItem.isLoading,
      originalAssets: (state) => state.mediaItem.assets,
    }),
    assets() {
      if (this.displayEditableAssetsDuringProcessing) {
        return this.transcodingAssets;
      }

      let sortedAssets = [];
      let fileteredAssets = this.originalAssets.filter((item) => item.type === 'image');

      if (this.mediaItem.orientation === 'vertical') {
        const posterImage9x16 = fileteredAssets.findIndex((item) => item.assetRole.shortName === 'posterImage9x16');
        const posterImage2x3 = fileteredAssets.findIndex((item) => item.assetRole.shortName === 'posterImage2x3');
        const posterImage16x9 = fileteredAssets.findIndex((item) => item.assetRole.shortName === 'posterImage16x9');

        if (posterImage9x16 > -1) {
          sortedAssets.push(fileteredAssets[posterImage9x16]);
          fileteredAssets[posterImage9x16] = null;
        }
        if (posterImage2x3 > -1) {
          sortedAssets.push(fileteredAssets[posterImage2x3]);
          fileteredAssets[posterImage2x3] = null;
        }
        if (posterImage16x9 > -1) {
          sortedAssets.push(fileteredAssets[posterImage16x9]);
          fileteredAssets[posterImage16x9] = null;
        }

        fileteredAssets = fileteredAssets.filter((item) => item !== null);
        sortedAssets = sortedAssets.concat(fileteredAssets);
      } else {
        sortedAssets = fileteredAssets;
      }
      return sortedAssets;
    },
    hasUnsavedChanges() {
      return this.itemsWithChanges.length > 0;
    },
    displayEditableAssetsDuringProcessing() {
      return this.mediaItem.status === 'transcoding' && this.mediaItem.type !== 'live';
    },
    transcodingAssets() {
      const posterImage = imageConstraints['assets-static-posterImage'];
      posterImage.url = this.mediaItem.posterImage || '';

      const posterImage2x3 = imageConstraints['assets-static-posterImage2x3'];
      posterImage2x3.url = this.mediaItem.posterImage2x3 || '';

      const posterImage9x16 = imageConstraints['assets-static-posterImage9x16'];
      posterImage9x16.url = this.mediaItem.posterImage9x16 || '';

      if (this.mediaItem.orientation === 'vertical') {
        return [posterImage9x16, posterImage2x3, posterImage];
      }

      return [posterImage, posterImage2x3];
    },
  },
  watch: {
    imageItemToHilight(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const element = this.$refs.assetImage.find((el) => el.source.id === newVal.id);
          if (element && element.$el && element.$el.scrollIntoView) {
            element.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          this.clearImageItemToHilight();
        });
      }
    },
    hasUnsavedChanges(newVal) {
      if (!newVal) {
        this.clearImagesTabWarn();
      }
    },
  },
  created() {
    this.$store.dispatch(GET_ASSET_LANGUAGE_REQUEST);
  },
  destroyed() {

  },
  methods: {
    setAssetIsInProcessing(item) {
      this.assetItemInProcessing = item;
    },
    addAssetItemWithChanges(item) {
      if (this.itemsWithChanges.includes(item)) {
        this.itemsWithChanges = this.itemsWithChanges.filter((el) => el !== item);
      } else {
        this.itemsWithChanges.push(item);
      }
    },
    removeAssetItemWithChanges(item) {
      this.itemsWithChanges = this.itemsWithChanges.filter((el) => el !== item);
    },
  },
};
</script>

<style scoped>
.images-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  height: 100%;
}
.no-images{
  position: relative;
  width: 100%;
  height: 50vh;
  display: flex;
  justify-content: center;
  padding-top: 40px;
}
.image-container {
  width: fit-content;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  border: 1px solid #43434d;
  padding: 30px;
  gap: 20px;
  position: relative;
}
.unsaved-changes-warning {
  color: #F2C94C;
  font-size: 14px;
  padding: 8px 10px;
  border: 1px solid #F2C94C;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
